import { config } from '@accounts/config';
import { Legal } from '@accounts/domain';
import { languages, type Language } from '@accounts/locale';
import { usePresence } from '@accounts/session';
import { Select } from '@beyond-js/ui/react';

/**
 * What closes the sign-in screen: the terms and privacy policy, and the language.
 *
 * The terms and policy are linked before anyone signs in, in the language chosen here, and only once
 * they are published; the notice appears when the service says which versions it records
 * (`presented`). The language selector is here, before any session exists, because someone who cannot
 * read the screen cannot sign in to change their preference.
 */
export function Footer({ presented }: { presented: boolean }) {
	const { text, language, choose } = usePresence();
	const legal = new Legal(config.legal);

	return (
		<>
			{legal.published ? (
				<nav aria-label={text('legal.label')} className="flex flex-col items-center gap-2 text-center text-small text-ink-muted">
					{presented ? <p className="max-w-reading">{text('legal.notice')}</p> : null}
					<div className="flex flex-wrap justify-center gap-4">
						<a className="inline-flex min-h-[44px] items-center underline" href={legal.address('privacy', language)}>
							{text('legal.privacy')}
						</a>
						<a className="inline-flex min-h-[44px] items-center underline" href={legal.address('terms', language)}>
							{text('legal.terms')}
						</a>
					</div>
				</nav>
			) : null}

			<div className="flex items-center justify-center gap-2 border-t border-line pt-4">
				<label htmlFor="signin-language" className="text-label text-ink-muted">
					{text('signin.language')}
				</label>
				<Select
					id="signin-language"
					value={language}
					onChange={event => void choose(event.target.value as Language)}
					options={languages.map(code => ({ value: code, label: text(`language.${code}`) }))}
				/>
			</div>
		</>
	);
}
